import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

/**
 * Header Component
 * Navbar matching the design from sangabrielconsulting.com
 * Logo on left, nav items centered, Contact Now button on right
 */
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const [openDropdown, setOpenDropdown] = useState(null);

  const navItems = [
    { label: 'Home', to: '/' },
    {
      label: 'Services',
      dropdown: [
        { label: 'Advertise', to: '/advertise' },
        { label: 'Signage & Wraps', to: '/signage-wraps' },
        { label: 'Vehicle Partner', to: '/vehicle-partner' },
      ]
    },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Revenue Per Vehicle', to: '/revenue-per-vehicle' },
    {
      label: 'Owner-Operator Software',
      to: '/owner-operator-software',
      badge: 'FIRST 100 FREE',
      highlight: true
    },
    { label: 'Case Studies', to: '/case-studies' },
    { label: 'Contact', to: '/contact' },
  ];

  const isActiveRoute = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${isScrolled ? 'shadow-md' : ''
        }`}
    >
      {/* Main navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/">
              <img
                src="/SGS-LOGO.png"
                alt="San Gabriel Solutions"
                className="h-14 w-auto"
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navItems.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
                onMouseEnter={() => item.dropdown && setOpenDropdown(item.label)}
                onMouseLeave={() => item.dropdown && setOpenDropdown(null)}
              >
                {item.dropdown ? (
                  // Dropdown menu item
                  <div>
                    <button
                      className="text-sm font-medium transition-colors py-2 inline-flex items-center gap-1 text-neutral-600 hover:text-primary-900"
                    >
                      {item.label}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown menu */}
                    <AnimatePresence>
                      {openDropdown === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="absolute top-full left-0 mt-2 w-max min-w-[12rem] whitespace-nowrap bg-white rounded-lg shadow-xl border border-neutral-100 py-2 z-50 overflow-hidden"
                        >
                          {item.dropdown.map((dropdownItem, idx) => (
                            <Link
                              key={dropdownItem.to}
                              to={dropdownItem.to}
                              className={`group block px-5 py-3 text-sm font-medium transition-all duration-200 relative ${isActiveRoute(dropdownItem.to)
                                ? 'text-primary-900 bg-gradient-to-r from-primary-50 to-primary-100/50'
                                : 'text-neutral-700 hover:text-primary-900 hover:bg-gradient-to-r hover:from-neutral-50 hover:to-transparent'
                                }`}
                            >
                              <div className="flex items-center">
                                <span className="relative z-10">{dropdownItem.label}</span>
                              </div>
                              {isActiveRoute(dropdownItem.to) && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-500 to-primary-600 rounded-r" />
                              )}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  // Regular nav item
                  <Link
                    to={item.to}
                    className={`text-sm font-medium transition-colors py-2 inline-flex items-center gap-2 ${isActiveRoute(item.to)
                      ? 'text-primary-900 font-semibold'
                      : item.highlight
                        ? 'text-accent-600 hover:text-accent-700'
                        : 'text-neutral-600 hover:text-primary-900'
                      }`}
                  >
                    {item.label}
                    {item.badge && (
                      <span className="inline-flex items-center gap-1.5 ml-1">
                        <span className="relative inline-flex items-center gap-1 px-2.5 py-1 text-[9px] font-black tracking-wider text-white uppercase bg-gradient-to-br from-orange-600 to-red-600 rounded shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200" style={{ clipPath: 'polygon(0 0, 100% 0, 95% 100%, 0% 100%)' }}>
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                          </svg>
                          <span>{item.badge}</span>
                        </span>
                      </span>
                    )}
                  </Link>
                )}
                {/* Active indicator underline */}
                {!item.dropdown && isActiveRoute(item.to) && (
                  <motion.div
                    layoutId="activeNavIndicator"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent-500 rounded-full"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.div>
            ))}
          </nav>

          {/* Desktop CTA Button */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="hidden lg:block"
          >
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-2.5 bg-primary-900 text-white text-sm font-medium rounded-md hover:bg-primary-800 transition-colors"
            >
              Schedule a Consultation
            </Link>
          </motion.div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-primary-900 hover:bg-neutral-100 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Bottom border line */}
      <div className="h-[3px] bg-gradient-to-r from-[#1e3a5f] via-[#3b6ea5] to-[#1e3a5f]" />

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-b border-neutral-200 shadow-lg"
          >
            <div className="py-4 space-y-1 px-4">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.dropdown ? (
                    // Dropdown in mobile
                    <div>
                      <div className="px-4 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                        {item.label}
                      </div>
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.to}
                          to={dropdownItem.to}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block px-6 py-3 rounded-lg transition-colors text-sm font-medium min-h-[44px] flex items-center touch-manipulation ${isActiveRoute(dropdownItem.to)
                            ? 'text-primary-900 bg-primary-50 border-l-4 border-accent-500 font-semibold'
                            : 'text-neutral-600 hover:text-primary-900 hover:bg-neutral-50 active:bg-neutral-100 border-l-4 border-transparent'
                            }`}
                        >
                          {dropdownItem.label}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    // Regular mobile nav item
                    <Link
                      to={item.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-4 py-3 rounded-lg transition-colors text-sm font-medium min-h-[44px] flex items-center justify-between touch-manipulation ${isActiveRoute(item.to)
                        ? 'text-primary-900 bg-primary-50 border-l-4 border-accent-500 font-semibold'
                        : 'text-neutral-600 hover:text-primary-900 hover:bg-neutral-50 active:bg-neutral-100 border-l-4 border-transparent'
                        }`}
                    >
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className="px-2.5 py-1 text-[10px] font-extrabold tracking-wider bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-md shadow-md">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 mt-4 border-t border-neutral-200">
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-center px-6 py-3 bg-primary-900 text-white text-sm font-medium rounded-md hover:bg-primary-800 active:bg-primary-950 transition-colors min-h-[44px] flex items-center justify-center touch-manipulation"
                >
                  Schedule a Consultation
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
